import { useQuery } from '@tanstack/react-query';
import { apartmentDebugQuery, type ApartmentDebug } from '@/lib/apartment-debug';
import { PROVIDER_LABELS, type ApartmentChannelMapping, type ChannelSyncEvent } from '@/lib/channels';
import { relativeDateTime } from '@/lib/format';
import { errorMessage } from '@/lib/errors';
import { Field } from '@/components/apartment/OverviewSection';
import { Badge } from '@/components/ui/badge';

type OutboxRow = ApartmentDebug['outbox']['recent'][number];
type InboundEvent = ApartmentDebug['inbound_events'][number];

const DASH = '—';

// absoluteUTC — full datetime pinned to UTC (unlike lib/format's
// formatDateTime, which renders in the viewer's local zone). This panel is a
// mirror of raw backend/Channex state, so every timestamp here is shown in
// the zone it's stored in, paired with a relative age for quick scanning.
function absoluteUTC(dt: string): string {
  try {
    return new Intl.DateTimeFormat(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZone: 'UTC',
      timeZoneName: 'short',
    }).format(new Date(dt));
  } catch {
    return dt;
  }
}

// prettyPrintPayload — raw_payload is the inbound webhook body verbatim, as
// a string: Channex sends a JSON object for most events but a JSON ARRAY for
// `ari` events (the shape that used to 500 this endpoint when the field was
// typed as an object — see OH-41 follow-up). Pretty-print when it parses;
// fall back to String(raw) otherwise so a diagnostics panel never blanks out
// or throws — on malformed JSON, or even on a non-string value slipping
// through (this field is contractually a string, but a diagnostics panel
// mirroring odd backend data is exactly where a defensive coercion belongs).
function prettyPrintPayload(raw: string): string {
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return String(raw);
  }
}

const CARD = 'rounded-[var(--radius-card)] border border-[var(--border-hairline)] bg-card p-5';
const SUBCARD = 'rounded-[var(--radius-control)] border border-[var(--border-hairline)] bg-[var(--surface-sunken)] p-4';
const HEADING = 'text-xs font-semibold uppercase tracking-[0.06em] text-muted';

// DebugSection — the operator diagnostics panel (Debug tab). Read-only: it
// only calls apartmentDebugQuery, never a mutation. This is the one place in
// the apartment detail page where raw Channex identifiers and outbox/webhook
// rows reach the UI (Task 4 removed them from the operator-facing DTOs).
export function DebugSection({ apartmentID }: { apartmentID: string }) {
  const { data, error, isLoading } = useQuery(apartmentDebugQuery(apartmentID));
  const errMsg = error ? errorMessage(error) : null;

  return (
    <div className="space-y-5">
      {isLoading && <p className="text-sm text-muted">Loading…</p>}
      {errMsg && (
        <p
          role="alert"
          className="rounded-[var(--radius-control)] border border-[var(--ollio-danger)] bg-[var(--ollio-danger-bg)] px-4 py-3 text-sm text-destructive"
        >
          {errMsg}
        </p>
      )}
      {data && (
        <>
          <IdentifiersBlock debug={data} />
          <MappingsBlock mappings={data.mappings} />
          <OutboxBlock outbox={data.outbox} />
          <EventLogBlock syncEvents={data.sync_events} inboundEvents={data.inbound_events} />
        </>
      )}
    </div>
  );
}

function IdentifiersBlock({ debug }: { debug: ApartmentDebug }) {
  return (
    <section className={CARD}>
      <h2 className={`mb-4 ${HEADING}`}>Identifiers</h2>
      <dl className="grid grid-cols-1 gap-x-6 gap-y-3 text-sm md:grid-cols-2">
        <Field label="Apartment ID" value={<code>{debug.apartment_id}</code>} />
        <Field label="Code" value={<code>{debug.code}</code>} />
        <Field label="Channex room type ID" value={<code>{debug.channex_room_type_id ?? DASH}</code>} />
        <Field label="Channex property ID" value={<code>{debug.channex_property_id ?? DASH}</code>} />
      </dl>

      {debug.rate_plans.length > 0 && (
        <div className="mt-5">
          <h3 className={`mb-2 ${HEADING}`}>Rate plans</h3>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {debug.rate_plans.map((rp) => (
              <dl key={rp.id} className={`${SUBCARD} grid grid-cols-1 gap-y-2 text-sm`}>
                <Field label="Title" value={rp.title} />
                <Field label="Code" value={<code>{rp.code}</code>} />
                <Field label="ID" value={<code>{rp.id}</code>} />
                <Field label="Channex rate plan ID" value={<code>{rp.channex_rate_plan_id ?? DASH}</code>} />
              </dl>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

function SyncStatusBadge({ status }: { status: ApartmentChannelMapping['sync_status'] }) {
  if (status === 'error') return <Badge variant="danger" size="sm">Error</Badge>;
  if (status === 'synced') return <Badge variant="success" size="sm" dot>Synced</Badge>;
  return <Badge variant="neutral" size="sm">Pending</Badge>;
}

function MappingsBlock({ mappings }: { mappings: ApartmentChannelMapping[] }) {
  return (
    <section className={CARD}>
      <h2 className={`mb-4 ${HEADING}`}>Channel mappings</h2>
      {mappings.length === 0 ? (
        <p className="text-sm text-muted">No channel mappings on file.</p>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {mappings.map((m) => (
            <div key={m.id} className={SUBCARD}>
              <div className="mb-3 flex items-center justify-between gap-2">
                <h3 className="text-sm font-semibold text-foreground">
                  {PROVIDER_LABELS[m.provider] ?? m.provider}
                </h3>
                <SyncStatusBadge status={m.sync_status} />
              </div>
              <dl className="grid grid-cols-1 gap-y-2 text-xs">
                <Field label="Mapping ID" value={<code>{m.id}</code>} />
                <Field label="Connection ID" value={<code>{m.connection_id ?? DASH}</code>} />
                <Field label="External listing ID" value={<code>{m.external_listing_id ?? DASH}</code>} />
                <Field label="External room ID" value={<code>{m.external_room_id ?? DASH}</code>} />
                <Field label="External rate ID" value={<code>{m.external_rate_id ?? DASH}</code>} />
                <Field label="Enabled" value={m.enabled ? 'Yes' : 'No'} />
                <Field
                  label="Last synced"
                  value={m.last_synced_at ? absoluteUTC(m.last_synced_at) : DASH}
                />
                {m.last_error && <Field label="Last error" value={m.last_error} />}
                <Field
                  label="Disabled at"
                  value={m.disabled_at ? absoluteUTC(m.disabled_at) : DASH}
                />
                <Field label="Disabled by" value={<code>{m.disabled_by ?? DASH}</code>} />
              </dl>
              {Object.keys(m.settings ?? {}).length > 0 && (
                <div className="mt-2 min-w-0">
                  <p className={`mb-1 ${HEADING}`}>Settings</p>
                  <pre className="overflow-x-auto rounded-[var(--radius-control)] bg-card p-2 text-xs">
                    {JSON.stringify(m.settings, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function OutboxBlock({ outbox }: { outbox: ApartmentDebug['outbox'] }) {
  return (
    <section className={CARD}>
      <h2 className={`mb-4 ${HEADING}`}>Outbox</h2>
      <dl className="mb-4 grid grid-cols-1 gap-x-6 gap-y-3 text-sm md:grid-cols-2">
        <Field label="Pending" value={String(outbox.pending_count)} />
        <Field
          label="Oldest pending"
          value={
            outbox.oldest_pending_at
              ? `${absoluteUTC(outbox.oldest_pending_at)} (${relativeDateTime(outbox.oldest_pending_at)})`
              : DASH
          }
        />
      </dl>

      {outbox.recent.length === 0 ? (
        <p className="text-sm text-muted">No recent outbox activity.</p>
      ) : (
        <div className="min-w-0 overflow-x-auto">
          <table className="w-full min-w-[760px] text-left text-xs">
            <thead>
              <tr className="border-b border-[var(--border-hairline)] text-muted">
                <th className="py-2 pr-3 font-medium">Event</th>
                <th className="py-2 pr-3 font-medium">Aggregate</th>
                <th className="py-2 pr-3 font-medium">Status</th>
                <th className="py-2 pr-3 font-medium">Attempts</th>
                <th className="py-2 pr-3 font-medium">Next attempt</th>
                <th className="py-2 pr-3 font-medium">Last error</th>
                <th className="py-2 pr-3 font-medium">Channex task</th>
                <th className="py-2 pr-3 font-medium">Created</th>
                <th className="py-2 pr-3 font-medium">Sent</th>
              </tr>
            </thead>
            <tbody>
              {outbox.recent.map((r) => (
                <OutboxRowView key={r.id} row={r} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function OutboxRowView({ row }: { row: OutboxRow }) {
  return (
    <tr className="border-b border-[var(--border-hairline)] last:border-0">
      <td className="py-2 pr-3">{row.event_type}</td>
      <td className="py-2 pr-3">{row.aggregate_kind}</td>
      <td className="py-2 pr-3">{row.status}</td>
      <td className="py-2 pr-3">{row.attempts}</td>
      <td className="py-2 pr-3">{row.next_attempt_at ? absoluteUTC(row.next_attempt_at) : DASH}</td>
      <td className="py-2 pr-3">{row.last_error ?? DASH}</td>
      <td className="py-2 pr-3"><code>{row.channex_task_id ?? DASH}</code></td>
      {/* created_at is schema-optional but always populated by the backend
          (see Task 7 brief); the fallback below only guards against a shape
          the backend does not actually produce. */}
      <td className="py-2 pr-3">{row.created_at ? absoluteUTC(row.created_at) : DASH}</td>
      <td className="py-2 pr-3">{row.sent_at ? absoluteUTC(row.sent_at) : DASH}</td>
    </tr>
  );
}

function EventLogBlock({
  syncEvents,
  inboundEvents,
}: {
  syncEvents: ChannelSyncEvent[];
  inboundEvents: InboundEvent[];
}) {
  return (
    <section className={CARD}>
      <h2 className={`mb-4 ${HEADING}`}>Event log</h2>

      <h3 className={`mb-2 ${HEADING}`}>Sync events</h3>
      {syncEvents.length === 0 ? (
        <p className="mb-4 text-sm text-muted">No sync events.</p>
      ) : (
        <div className="mb-5 space-y-2">
          {syncEvents.map((e) => (
            <details key={e.id} className={SUBCARD}>
              <summary className="cursor-pointer text-sm">
                <span className="font-medium">{e.event_type}</span>{' '}
                <span className="text-xs text-muted">
                  {e.severity} · {absoluteUTC(e.occurred_at)}
                </span>
              </summary>
              <div className="mt-2 min-w-0">
                <dl className="mb-2 grid grid-cols-1 gap-y-1 text-xs md:grid-cols-3">
                  <Field
                    label="Provider"
                    value={e.provider ? (PROVIDER_LABELS[e.provider] ?? e.provider) : DASH}
                  />
                  <Field label="Connection ID" value={<code>{e.connection_id ?? DASH}</code>} />
                  <Field
                    label="Channex property ID"
                    value={<code>{e.channex_property_id ?? DASH}</code>}
                  />
                </dl>
                <pre className="overflow-x-auto rounded-[var(--radius-control)] bg-card p-2 text-xs">
                  {JSON.stringify(e.payload, null, 2)}
                </pre>
              </div>
            </details>
          ))}
        </div>
      )}

      <h3 className={`mb-2 ${HEADING}`}>Inbound webhook events</h3>
      {inboundEvents.length === 0 ? (
        <p className="text-sm text-muted">No inbound webhook events.</p>
      ) : (
        <div className="space-y-2">
          {inboundEvents.map((e) => (
            <details key={e.id} className={SUBCARD}>
              <summary className="cursor-pointer text-sm">
                <span className="font-medium">{e.event_type}</span>{' '}
                <span className="text-xs text-muted">
                  {absoluteUTC(e.received_at)}
                  {e.processed_at ? ` → processed ${absoluteUTC(e.processed_at)}` : ' · unprocessed'}
                </span>
              </summary>
              <div className="mt-2 min-w-0">
                <pre className="overflow-x-auto rounded-[var(--radius-control)] bg-card p-2 text-xs">
                  {prettyPrintPayload(e.raw_payload)}
                </pre>
              </div>
            </details>
          ))}
        </div>
      )}
    </section>
  );
}
